import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  useTheme,
  Skeleton,
  Avatar,
  IconButton,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  TextField,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Groups as GroupsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import { ActionModal } from "../shared";
import { DeleteConfirmationDialog } from "../UserManagement";
import SearchField from "../shared/SearchField";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import { phoneMatchesSearch } from "../../utils/phoneUtils";
import { lightTheme } from "../../styles/theme";
import { useSchoolUsers, useCircleUsers } from "../../hooks/useUserData";
import {
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
  MANAGEMENT_ACTION_ROW_SX,
} from "../../styles/managementCardActions";
import {
  MANAGEMENT_CARD_HEADER_FONT_SIZE,
  MANAGEMENT_CARD_META_FONT_SIZE,
  MANAGEMENT_CARD_TITLE_FONT_SIZE,
} from "../../styles/managementCardTypography";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
} from "../../styles/managementShared";

interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  status: "active" | "inactive";
  joinedAt: string;
}

interface SubcircleUser {
  userId: string;
  email: string;
  name: string;
  phoneNumber?: string;
  alias?: string;
  role: string;
  joinedAt: string;
}

interface ManageCircleUsersProps {
  open: boolean;
  onClose: () => void;
  subcircleId: string;
  subcircleName: string;
  onUsersChanged?: () => void;
  /** Full-width page (no dialog). Desktop circle members route uses this. */
  layout?: "modal" | "page";
  /** A User to start adding (by id): opens Add user for them to confirm, once loaded. Saves nothing itself. */
  startAdding?: string;
}

/** Initial batch size for infinite scrolling lists. */
const USERS_BATCH_SIZE = 5;

/** Vertical gap between list cards (~12px); shared by add + members columns. */
const MEMBER_LIST_CARD_GAP = 1.5;
const MEMBER_CARD_AVATAR_SIZE = 48;
/** Sm+ min height so shorter rows (add) align with taller member rows. */
const MEMBER_CARD_MIN_HEIGHT_SM = 132;

/**
 * Mirrors `UserList` column structure; last track is a bit wider than `0.5fr`
 * so “Edit alias” + trash action fit without crowding data columns.
 */
const DESKTOP_MEMBERS_GRID_COLUMNS =
  "minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1fr)";

/** Desktop “Add users” table: name, email, phone, add action. */
const DESKTOP_ADD_USERS_GRID_COLUMNS =
  "minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr)";

const membersDesktopHeaderCellSx = {
  fontWeight: 600,
  color: "text.secondary",
  textTransform: "uppercase" as const,
  fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
  letterSpacing: "0.5px",
};

const memberListCardSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "space-between",
  gap: { xs: 1.5, sm: 2 },
  p: { xs: 2, sm: 2.5 },
  minHeight: { xs: "auto", sm: MEMBER_CARD_MIN_HEIGHT_SM },
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
  boxShadow: "none",
  transition: "background-color 0.15s ease",
  "&:hover": {
    bgcolor: "action.hover",
  },
};

/** Compact row inside “Discover & add” panel. */
const addUserHubRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  transition: "background-color 0.15s ease",
  "&:hover": {
    bgcolor: "action.hover",
  },
};

// Avatar utilities are now imported from shared utils

export const ManageCircleUsers = React.memo<ManageCircleUsersProps>(
  ({
    open,
    onClose,
    subcircleId,
    subcircleName,
    onUsersChanged,
    layout = "modal",
    startAdding,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isPageLayout = layout === "page";
    const { user, token } = useAuth();

    // UI state
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [addingUserId, setAddingUserId] = useState<string | null>(null);
    const [removingUserId, setRemovingUserId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    /** Filter for current-members column (page hub only; modal keeps single top search for add). */
    const [membersSearchQuery, setMembersSearchQuery] = useState("");
    const [addUsersExpanded, setAddUsersExpanded] = useState(true);
    const [currentMembersExpanded, setCurrentMembersExpanded] = useState(true);
    const [legacyAccessOption, setLegacyAccessOption] = useState<
      "none" | "7days" | "all"
    >("7days"); // Default to 7 days
    /** User chosen via Add — confirm historical access in modal before API call. */
    const [userPendingAdd, setUserPendingAdd] = useState<User | null>(null);
    const [pendingAddAlias, setPendingAddAlias] = useState("");
    const [visibleAddUsersCount, setVisibleAddUsersCount] =
      useState(USERS_BATCH_SIZE);
    const [visibleMembersCount, setVisibleMembersCount] =
      useState(USERS_BATCH_SIZE);
    const [aliasDrafts, setAliasDrafts] = useState<Record<string, string>>({});
    const [savingAliasUserId, setSavingAliasUserId] = useState<string | null>(null);
    const [desktopTab, setDesktopTab] = useState<"add-users" | "current-members">(
      "add-users"
    );
    const [aliasDialogUser, setAliasDialogUser] = useState<SubcircleUser | null>(
      null
    );
    const [aliasDialogValue, setAliasDialogValue] = useState("");
    const [userPendingRemove, setUserPendingRemove] =
      useState<SubcircleUser | null>(null);
    const aliasDraftsRef = useRef<Record<string, string>>({});
    const savedAliasByUserRef = useRef<Record<string, string>>({});
    const aliasDebounceRef = useRef<
      Record<string, ReturnType<typeof setTimeout>>
    >({});
    const addUsersSentinelRef = useRef<HTMLDivElement | null>(null);
    const membersSentinelRef = useRef<HTMLDivElement | null>(null);

    aliasDraftsRef.current = aliasDrafts;

    // Use custom hooks for data management
    const {
      schoolUsers,
      isLoading: schoolUsersLoading,
      refetch: refetchSchoolUsers,
    } = useSchoolUsers();
    const {
      circleUsers,
      isLoading: circleUsersLoading,
      refetch: refetchCircleUsers,
    } = useCircleUsers(subcircleId);

    const isLoading = schoolUsersLoading || circleUsersLoading;

    useEffect(() => {
      if (open) {
        setVisibleAddUsersCount(USERS_BATCH_SIZE);
        setVisibleMembersCount(USERS_BATCH_SIZE);
        setMembersSearchQuery("");
        setUserPendingAdd(null);
        setDesktopTab("add-users");
      }
    }, [open, subcircleId]);

    useEffect(() => {
      if (!aliasDialogUser) return;
      setAliasDialogValue(
        aliasDraftsRef.current[aliasDialogUser.userId] ??
          aliasDialogUser.alias ??
          ""
      );
    }, [aliasDialogUser]);

    useEffect(() => {
      if (!open) {
        setAliasDrafts({});
        savedAliasByUserRef.current = {};
        Object.values(aliasDebounceRef.current).forEach(clearTimeout);
        aliasDebounceRef.current = {};
        setUserPendingAdd(null);
        return;
      }
      setAliasDrafts((prev) => {
        const next = { ...prev };
        for (const member of circleUsers) {
          if (next[member.userId] === undefined) {
            const a = member.alias || "";
            next[member.userId] = a;
            savedAliasByUserRef.current[member.userId] = a.trim();
          }
        }
        return next;
      });
    }, [open, circleUsers]);

    useEffect(() => {
      setVisibleAddUsersCount(USERS_BATCH_SIZE);
    }, [searchQuery]);

    useEffect(() => {
      setVisibleMembersCount(USERS_BATCH_SIZE);
    }, [membersSearchQuery, subcircleId, open]);

    const requestAddUser = (userToAdd: User) => {
      setError(null);
      setPendingAddAlias("");
      setUserPendingAdd(userToAdd);
    };

    const confirmAddPendingUser = async () => {
      const userToAdd = userPendingAdd;
      if (!userToAdd || !token || !user?.schoolId) {
        return;
      }

      const aliasTrimmed = pendingAddAlias.trim();
      if (aliasTrimmed.length > 60) {
        setError("Display alias must be 60 characters or less.");
        return;
      }

      setAddingUserId(userToAdd.id);
      setError(null);
      setSuccess(null);

      try {
        const response = await api.circles.addUserToSubCircle(
          {
            email: userToAdd.email,
            phoneNumber: userToAdd.phoneNumber,
            subcircleId,
            schoolId: user.schoolId!,
            accessLegacyMessages: legacyAccessOption === "all",
            legacyAccessDays: legacyAccessOption === "7days" ? 7 : undefined,
            alias: aliasTrimmed || undefined,
          },
          token
        );

        setUserPendingAdd(null);

        if (response.status === "pending" || response.invitationId) {
          setSuccess(
            `Invitation sent to ${userToAdd.name}. They'll be automatically added to ${subcircleName} when they sign up.`
          );
        } else {
          setSuccess(
            `${userToAdd.name} successfully added to ${subcircleName}`
          );
        }

        await Promise.all([refetchSchoolUsers(), refetchCircleUsers()]);

        onUsersChanged?.();

        setTimeout(() => setSuccess(null), 5000);
      } catch (err: any) {
        console.error("Failed to add user to circle:", err);
        setError(
          err.message || "Failed to add user to circle. Please try again."
        );
      } finally {
        setAddingUserId(null);
      }
    };

    // Remove user from circle — returns true on success (for confirm modal flow).
    const handleRemoveUser = async (
      userToRemove: SubcircleUser
    ): Promise<boolean> => {
      if (!token || !user?.schoolId) {
        setError("Authentication required. Please log in again.");
        return false;
      }

      setRemovingUserId(userToRemove.userId);
      setError(null);
      setSuccess(null);

      try {
        await api.circles.removeUserFromSubCircle(
          {
            userId: userToRemove.userId,
            subcircleId,
            schoolId: user.schoolId!,
          },
          token
        );

        setSuccess(
          `${userToRemove.name} successfully removed from ${subcircleName}`
        );

        // Refresh both lists
        await Promise.all([refetchSchoolUsers(), refetchCircleUsers()]);

        if (onUsersChanged) {
          onUsersChanged();
        }

        setTimeout(() => setSuccess(null), 3000);
        return true;
      } catch (err: any) {
        console.error("Failed to remove user from circle:", err);
        setError(
          err.message || "Failed to remove user from circle. Please try again."
        );
        return false;
      } finally {
        setRemovingUserId(null);
      }
    };

    const handleAliasDraftChange = (userId: string, value: string) => {
      setAliasDrafts((prev) => ({
        ...prev,
        [userId]: value,
      }));
    };

    const persistMemberAlias = useCallback(
      async (userId: string) => {
        if (!token || !user?.schoolId) return;
        const circleUser = circleUsers.find((c) => c.userId === userId);
        if (!circleUser) return;

        const raw = aliasDraftsRef.current[userId] ?? "";
        const trimmed = raw.trim();
        if (trimmed.length > 60) {
          setError("Alias must be 60 characters or less.");
          return;
        }

        const lastSaved = savedAliasByUserRef.current[userId] ?? "";
        if (trimmed === lastSaved) return;

        setSavingAliasUserId(userId);
        setError(null);

        try {
          await api.circles.updateUserAliasInSubCircle(
            {
              userId,
              subcircleId,
              schoolId: user.schoolId,
              alias: trimmed,
            },
            token
          );

          savedAliasByUserRef.current[userId] = trimmed;
          await refetchCircleUsers();
          onUsersChanged?.();
        } catch (err: any) {
          console.error("Failed to save member alias:", err);
          setError(err.message || "Failed to save alias. Please try again.");
        } finally {
          setSavingAliasUserId(null);
        }
      },
      [
        token,
        user?.schoolId,
        subcircleId,
        circleUsers,
        refetchCircleUsers,
        onUsersChanged,
      ]
    );

    const flushAliasSave = useCallback(
      (userId: string) => {
        const t = aliasDebounceRef.current[userId];
        if (t) {
          clearTimeout(t);
          delete aliasDebounceRef.current[userId];
        }
        void persistMemberAlias(userId);
      },
      [persistMemberAlias]
    );

    const saveAliasFromDialog = useCallback(() => {
      if (!aliasDialogUser) return;
      const uid = aliasDialogUser.userId;
      handleAliasDraftChange(uid, aliasDialogValue);
      setAliasDialogUser(null);
      setTimeout(() => flushAliasSave(uid), 0);
    }, [aliasDialogUser, aliasDialogValue, flushAliasSave]);

    const scheduleAliasSave = useCallback(
      (userId: string) => {
        if (aliasDebounceRef.current[userId]) {
          clearTimeout(aliasDebounceRef.current[userId]);
        }
        aliasDebounceRef.current[userId] = setTimeout(() => {
          delete aliasDebounceRef.current[userId];
          void persistMemberAlias(userId);
        }, 700);
      },
      [persistMemberAlias]
    );

    // Optimize filtering with Set-based lookup
    const circleUserIds = useMemo(() => {
      return new Set(circleUsers.map((cu) => cu.userId));
    }, [circleUsers]);

    const availableUsers = useMemo(() => {
      if (!searchQuery.trim()) {
        return schoolUsers.filter((user) => !circleUserIds.has(user.id));
      }

      const query = searchQuery.toLowerCase();
      return schoolUsers.filter(
        (user) =>
          (user.name.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query) ||
            phoneMatchesSearch(user.phoneNumber, searchQuery)) &&
          !circleUserIds.has(user.id)
      );
    }, [schoolUsers, circleUserIds, searchQuery]);

    const filteredCircleUsers = useMemo(() => {
      const q = membersSearchQuery.trim().toLowerCase();
      if (!q) return circleUsers;
      return circleUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          phoneMatchesSearch(u.phoneNumber, membersSearchQuery) ||
          (u.alias && u.alias.toLowerCase().includes(q))
      );
    }, [circleUsers, membersSearchQuery]);

    // Started with someone to add: open Add user for them once, when both lists have loaded
    const startedAddingRef = useRef<string | null>(null);
    useEffect(() => {
      if (!open || !startAdding || isLoading || startedAddingRef.current === startAdding) return;
      const toAdd = schoolUsers.find((u) => u.id === startAdding);
      if (!toAdd) return;
      startedAddingRef.current = startAdding;
      if (!circleUserIds.has(toAdd.id)) requestAddUser(toAdd);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- requestAddUser only sets state
    }, [open, startAdding, isLoading, schoolUsers, circleUserIds]);

    const visibleAvailableUsers = useMemo(
      () => availableUsers.slice(0, visibleAddUsersCount),
      [availableUsers, visibleAddUsersCount]
    );

    const visibleCircleUsers = useMemo(() => {
      const source = isPageLayout ? filteredCircleUsers : circleUsers;
      return source.slice(0, visibleMembersCount);
    }, [isPageLayout, filteredCircleUsers, circleUsers, visibleMembersCount]);
    const membersSourceLength = isPageLayout
      ? filteredCircleUsers.length
      : circleUsers.length;

    useEffect(() => {
      if (isLoading) return;
      const node = addUsersSentinelRef.current;
      if (!node) return;
      if (visibleAvailableUsers.length >= availableUsers.length) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            setVisibleAddUsersCount((prev) =>
              Math.min(prev + USERS_BATCH_SIZE, availableUsers.length)
            );
          }
        },
        { rootMargin: "200px 0px" }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }, [
      isLoading,
      desktopTab,
      visibleAvailableUsers.length,
      availableUsers.length,
    ]);

    useEffect(() => {
      if (isLoading) return;
      const node = membersSentinelRef.current;
      if (!node) return;
      if (visibleCircleUsers.length >= membersSourceLength) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            setVisibleMembersCount((prev) =>
              Math.min(prev + USERS_BATCH_SIZE, membersSourceLength)
            );
          }
        },
        { rootMargin: "200px 0px" }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }, [
      isLoading,
      desktopTab,
      visibleCircleUsers.length,
      membersSourceLength,
    ]);

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    if (!isPageLayout && !open) {
      return null;
    }

    if (!user?.schoolId || !token) {
      const authAlert = (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            You must be logged in to manage circle users.
          </Alert>
        </Box>
      );
      if (isPageLayout) {
        return authAlert;
      }
      return (
        <ActionModal
          open={open}
          onClose={onClose}
          title={`Manage Users in ${subcircleName}`}
        >
          {authAlert}
        </ActionModal>
      );
    }

    const panelBody = (
        <Box
          sx={{
            p: isPageLayout ? { xs: 0, sm: 0 } : { xs: 1, sm: 3 },
            width: "100%",
          }}
        >
          {/* Success/Error Messages */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Modal: single search. Page hub: search lives in each panel. */}
          {!isPageLayout && (
            <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
              <SearchField
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search users by name, email, or phone..."
                sx={{ width: "100%" }}
              />
            </Box>
          )}

          {(() => {
            const memberFlex = (
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 2, sm: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { sm: "stretch" },
                }}
              >
            {/* Add Users Section - Left Side */}
            {(!isPageLayout || desktopTab === "add-users") && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
              {isPageLayout ? (
                <Box id="circle-discover-add">
                  <Box
                    sx={{
                      pt: { xs: 1.25, sm: 3 },
                      mb: { xs: 1.5, sm: 3 },
                      px: { xs: 0, sm: 3 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        alignItems: { sm: "stretch" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SearchField
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search by name, email, or phone..."
                          sx={{ width: "100%" }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ px: { xs: 0, sm: 3 }, pb: 3 }}>
                    {isLoading ? (
                      <>
                        {!isMobile && (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: DESKTOP_ADD_USERS_GRID_COLUMNS,
                              gap: 2,
                              px: 3,
                              py: 1,
                              mb: 1,
                            }}
                          >
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Name
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Email
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Phone
                            </Typography>
                            <Box />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: isMobile ? MEMBER_LIST_CARD_GAP : 2,
                          }}
                        >
                          {[1, 2, 3].map((i) =>
                            isMobile ? (
                              <Box
                                key={i}
                                sx={{
                                  ...addUserHubRowSx,
                                  pointerEvents: "none",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Skeleton
                                    variant="circular"
                                    width={40}
                                    height={40}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Skeleton
                                      width="45%"
                                      height={20}
                                      sx={{ mb: 0.5 }}
                                    />
                                    <Skeleton width="70%" height={16} />
                                  </Box>
                                  <Skeleton
                                    variant="circular"
                                    width={40}
                                    height={40}
                                    sx={{ flexShrink: 0 }}
                                  />
                                </Box>
                              </Box>
                            ) : (
                              <Paper
                                key={i}
                                elevation={0}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 2,
                                  boxShadow: "none",
                                  pointerEvents: "none",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      DESKTOP_ADD_USERS_GRID_COLUMNS,
                                    gap: 2,
                                    px: 3,
                                    py: 2,
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Skeleton
                                      variant="circular"
                                      width={MANAGEMENT_AVATAR_SIZE}
                                      height={MANAGEMENT_AVATAR_SIZE}
                                    />
                                    <Skeleton width="55%" height={22} />
                                  </Box>
                                  <Skeleton width="80%" height={20} />
                                  <Skeleton width="50%" height={20} />
                                  <Skeleton
                                    variant="circular"
                                    width={40}
                                    height={40}
                                    sx={{ flexShrink: 0, justifySelf: "end" }}
                                  />
                                </Box>
                              </Paper>
                            )
                          )}
                        </Box>
                      </>
                    ) : availableUsers.length === 0 ? (
                      <Box
                        sx={{ py: 5, textAlign: "center", color: "text.secondary" }}
                      >
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                          {searchQuery
                            ? "No users found matching your search."
                            : "All users are already in this circle."}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {!isMobile && (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: DESKTOP_ADD_USERS_GRID_COLUMNS,
                              gap: 2,
                              px: 3,
                              py: 1,
                              mb: 1,
                            }}
                          >
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Name
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Email
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Phone
                            </Typography>
                            <Box />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: isMobile ? MEMBER_LIST_CARD_GAP : 2,
                          }}
                        >
                        {visibleAvailableUsers.map((userToAdd) =>
                          isMobile ? (
                          <Box key={userToAdd.id} sx={addUserHubRowSx}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  flexShrink: 0,
                                  fontSize: "0.875rem",
                                  fontWeight: 600,
                                  backgroundColor: getAvatarColor(
                                    userToAdd.name,
                                    lightTheme
                                  ),
                                  color: "common.white",
                                }}
                              >
                                {getInitials(userToAdd.name)}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.9375rem",
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {userToAdd.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: "0.8125rem",
                                    lineHeight: 1.45,
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {userToAdd.email}
                                  {userToAdd.phoneNumber?.trim()
                                    ? ` · ${userToAdd.phoneNumber}`
                                    : ""}
                                </Typography>
                              </Box>
                            </Box>
                              <IconButton
                                color="primary"
                                size="medium"
                                onClick={() => requestAddUser(userToAdd)}
                                disabled={!!addingUserId || !!userPendingAdd}
                                aria-label="Add user"
                              >
                                <AddIcon />
                              </IconButton>
                          </Box>
                          ) : (
                            <Paper
                              key={userToAdd.id}
                              elevation={0}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    DESKTOP_ADD_USERS_GRID_COLUMNS,
                                  gap: 2,
                                  px: 3,
                                  py: 2,
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    minWidth: 0,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: MANAGEMENT_AVATAR_SIZE,
                                      height: MANAGEMENT_AVATAR_SIZE,
                                      flexShrink: 0,
                                      fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                                      fontWeight: 600,
                                      backgroundColor: getAvatarColor(
                                        userToAdd.name,
                                        lightTheme
                                      ),
                                      color: "common.white",
                                    }}
                                  >
                                    {getInitials(userToAdd.name)}
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE,
                                    }}
                                  >
                                    {userToAdd.name}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                                    minWidth: 0,
                                  }}
                                >
                                  {userToAdd.email || "—"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                                >
                                  {userToAdd.phoneNumber?.trim()
                                    ? userToAdd.phoneNumber
                                    : "—"}
                                </Typography>
                                <Box
                                  className="action-buttons"
                                  sx={MANAGEMENT_ACTION_ROW_SX}
                                >
                                  <IconButton
                                    color="primary"
                                    size="medium"
                                    onClick={() => requestAddUser(userToAdd)}
                                    disabled={
                                      !!addingUserId || !!userPendingAdd
                                    }
                                    aria-label="Add user"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          )
                        )}
                        {visibleAvailableUsers.length < availableUsers.length && (
                          <Box
                            ref={addUsersSentinelRef}
                            sx={{ mt: 2, pt: 1, textAlign: "center", color: "text.secondary" }}
                          >
                            <Typography variant="caption">Loading more...</Typography>
                          </Box>
                        )}
                      </Box>
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                <Accordion
                  expanded={addUsersExpanded}
                  onChange={(_, isExpanded) => setAddUsersExpanded(isExpanded)}
                  sx={{
                    boxShadow: "none",
                    border: { xs: "none", sm: "1px solid" },
                    borderColor: { sm: "divider" },
                    borderRadius: { xs: 0, sm: 2 },
                    backgroundColor: "background.paper",
                    "&:before": { display: "none" },
                  }}
                >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 1, sm: 1.5 },
                    cursor: "pointer",
                    "& .MuiAccordionSummary-content": {
                      my: 0,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    Add Users to Circle ({availableUsers.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ px: { xs: 0, sm: 2 }, pt: 0, pb: { xs: 1, sm: 2 } }}
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: MEMBER_LIST_CARD_GAP,
                      }}
                    >
                      {[1, 2, 3].map((i) => (
                        <Paper
                          key={i}
                          elevation={0}
                          sx={{
                            ...memberListCardSx,
                            minHeight: { xs: MEMBER_CARD_MIN_HEIGHT_SM, sm: MEMBER_CARD_MIN_HEIGHT_SM },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={MEMBER_CARD_AVATAR_SIZE}
                              height={MEMBER_CARD_AVATAR_SIZE}
                            />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Skeleton
                                variant="text"
                                width="55%"
                                height={22}
                                sx={{ mb: 0.5 }}
                              />
                              <Skeleton
                                variant="text"
                                width="35%"
                                height={16}
                                sx={{ mb: 0.5 }}
                              />
                              <Skeleton
                                variant="text"
                                width="85%"
                                height={18}
                                sx={{ mb: 0.5 }}
                              />
                              <Skeleton variant="text" width="40%" height={18} />
                            </Box>
                            <Skeleton
                              variant="circular"
                              width={40}
                              height={40}
                              sx={{ flexShrink: 0 }}
                            />
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  ) : availableUsers.length === 0 ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: (theme) => theme.typography.body2.fontSize,
                          opacity: 0.7,
                        }}
                      >
                        {searchQuery
                          ? "No users found matching your search."
                          : "All users are already in this circle."}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: MEMBER_LIST_CARD_GAP,
                      }}
                    >
                      {visibleAvailableUsers.map((userToAdd) => (
                        <Paper
                          key={userToAdd.id}
                          elevation={0}
                          sx={memberListCardSx}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: { xs: "flex-start", sm: "center" },
                              gap: 2,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: MEMBER_CARD_AVATAR_SIZE,
                                height: MEMBER_CARD_AVATAR_SIZE,
                                flexShrink: 0,
                                backgroundColor: getAvatarColor(
                                  userToAdd.name,
                                  lightTheme
                                ),
                                color: "common.white",
                                fontSize: "0.9375rem",
                                fontWeight: 600,
                              }}
                            >
                              {getInitials(userToAdd.name)}
                            </Avatar>
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                component="div"
                                sx={{
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  color: "text.primary",
                                }}
                              >
                                {userToAdd.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2 }}
                              >
                                Eligible to invite
                              </Typography>
                              <Typography
                                variant="body2"
                                component="div"
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.8125rem",
                                  lineHeight: 1.45,
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {userToAdd.email}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}
                              >
                                {userToAdd.phoneNumber?.trim()
                                  ? userToAdd.phoneNumber
                                  : "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: { xs: "flex-end", sm: "center" },
                              flexShrink: 0,
                              pt: { xs: 0.5, sm: 0 },
                            }}
                          >
                            <IconButton
                              color="primary"
                              size="medium"
                              onClick={() => requestAddUser(userToAdd)}
                              disabled={
                                !!addingUserId || !!userPendingAdd
                              }
                              aria-label="Add user"
                              sx={{ p: 1 }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                      {visibleAvailableUsers.length < availableUsers.length && (
                        <Box
                          ref={addUsersSentinelRef}
                          sx={{ mt: 2, pt: 1, textAlign: "center", color: "text.secondary" }}
                        >
                          <Typography variant="caption">Loading more...</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              )}
              </Box>
            )}

            {/* Current Members Section - Right Side */}
            {(!isPageLayout || desktopTab === "current-members") && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  /* Tabbed page hub: only one panel visible — no extra top inset vs Add tab. */
                  mt: { xs: isPageLayout ? 0 : 2, sm: 0 },
                }}
              >
              {isPageLayout ? (
                <Box sx={{ minHeight: { sm: 420 } }}>
                  <Box
                    sx={{
                      pt: { xs: 1.25, sm: 3 },
                      mb: { xs: 1.5, sm: 3 },
                      px: { xs: 0, sm: 3 },
                    }}
                  >
                    <SearchField
                      value={membersSearchQuery}
                      onChange={setMembersSearchQuery}
                      placeholder="Search members..."
                      sx={{ width: "100%" }}
                    />
                  </Box>
                  <Box sx={{ px: { xs: 0, sm: 3 }, pb: 3 }}>
                    {circleUsers.length === 0 ? (
                      <Box
                        sx={{
                          py: 6,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        <GroupsIcon
                          sx={{
                            fontSize: 44,
                            mb: 2,
                            opacity: 0.25,
                            color: "text.secondary",
                          }}
                        />
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                          No users in this circle yet.
                        </Typography>
                      </Box>
                    ) : filteredCircleUsers.length === 0 ? (
                      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        <Typography variant="body2">
                          No members match your search.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {!isMobile && (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: DESKTOP_MEMBERS_GRID_COLUMNS,
                              gap: 2,
                              px: 3,
                              py: 1,
                              mb: 1,
                            }}
                          >
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Name
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Email
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Phone
                            </Typography>
                            <Typography sx={membersDesktopHeaderCellSx}>
                              Date Joined
                            </Typography>
                            <Box />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: isMobile ? MEMBER_LIST_CARD_GAP : 2,
                          }}
                        >
                          {visibleCircleUsers.map((circleUser) => {
                            const aliasDisplay = (
                              aliasDrafts[circleUser.userId] ??
                              circleUser.alias ??
                              ""
                            ).trim();
                            return (
                              <Paper
                                key={circleUser.userId}
                                elevation={0}
                                sx={
                                  isMobile
                                    ? memberListCardSx
                                    : {
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        boxShadow: "none",
                                        "&:hover": {
                                          backgroundColor: "action.hover",
                                        },
                                      }
                                }
                              >
                                {isMobile ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1.5,
                                      width: "100%",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        width: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1.5,
                                          flex: 1,
                                          minWidth: 0,
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <Avatar
                                          sx={{
                                            width: MEMBER_CARD_AVATAR_SIZE,
                                            height: MEMBER_CARD_AVATAR_SIZE,
                                            flexShrink: 0,
                                            mt: 0.125,
                                            backgroundColor: getAvatarColor(
                                              circleUser.name,
                                              lightTheme
                                            ),
                                            color: "common.white",
                                            fontSize: "0.9375rem",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {getInitials(circleUser.name)}
                                        </Avatar>
                                        <Box
                                          sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            component="div"
                                            sx={{
                                              fontWeight: 600,
                                              lineHeight: 1.3,
                                              color: "text.primary",
                                            }}
                                          >
                                            {circleUser.name}
                                          </Typography>
                                          {aliasDisplay ? (
                                            <Typography
                                              variant="caption"
                                              color="primary.main"
                                              sx={{ lineHeight: 1.3 }}
                                            >
                                              Display: {aliasDisplay}
                                            </Typography>
                                          ) : null}
                                          <Typography
                                            variant="body2"
                                            component="div"
                                            color="text.secondary"
                                            noWrap
                                            title={
                                              circleUser.email?.trim()
                                                ? circleUser.email
                                                : undefined
                                            }
                                            sx={{
                                              fontSize: "0.8125rem",
                                              lineHeight: 1.45,
                                            }}
                                          >
                                            {circleUser.email || "—"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                              fontSize: "0.8125rem",
                                              lineHeight: 1.45,
                                            }}
                                          >
                                            {circleUser.phoneNumber?.trim()
                                              ? circleUser.phoneNumber
                                              : "—"}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          lineHeight: 1.2,
                                          textAlign: "right",
                                          flexShrink: 0,
                                          maxWidth: "42%",
                                          pt: 0.125,
                                        }}
                                      >
                                        Joined {formatDate(circleUser.joinedAt)}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        pt: 0.25,
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                      }}
                                    >
                                      <Button
                                        variant="text"
                                        size="small"
                                        onClick={() =>
                                          setAliasDialogUser(circleUser)
                                        }
                                        sx={{
                                          minWidth: "auto",
                                          px: 0.75,
                                          textTransform: "none",
                                          fontSize: "0.8125rem",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Edit alias
                                      </Button>
                                      <IconButton
                                        className="delete-button"
                                        onClick={() =>
                                          setUserPendingRemove(circleUser)
                                        }
                                        disabled={
                                          removingUserId === circleUser.userId
                                        }
                                        color="error"
                                        size="small"
                                        aria-label={`Remove ${circleUser.name} from circle`}
                                        sx={{ p: 0.5, flexShrink: 0 }}
                                      >
                                        {removingUserId === circleUser.userId ? (
                                          <CircularProgress
                                            size={18}
                                            thickness={5}
                                          />
                                        ) : (
                                          <DeleteIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns:
                                        DESKTOP_MEMBERS_GRID_COLUMNS,
                                      gap: 2,
                                      px: 3,
                                      py: 2,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        minWidth: 0,
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          width: MANAGEMENT_AVATAR_SIZE,
                                          height: MANAGEMENT_AVATAR_SIZE,
                                          flexShrink: 0,
                                          backgroundColor: getAvatarColor(
                                            circleUser.name,
                                            lightTheme
                                          ),
                                          color: "common.white",
                                          fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {getInitials(circleUser.name)}
                                      </Avatar>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            fontSize:
                                              MANAGEMENT_CARD_TITLE_FONT_SIZE,
                                          }}
                                        >
                                          {circleUser.name}
                                        </Typography>
                                        {aliasDisplay ? (
                                          <Typography
                                            variant="caption"
                                            color="primary.main"
                                            sx={{ display: "block" }}
                                          >
                                            Display: {aliasDisplay}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      noWrap
                                      sx={{
                                        fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                                        minWidth: 0,
                                      }}
                                    >
                                      {circleUser.email || "—"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      noWrap
                                      sx={{
                                        fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                                      }}
                                    >
                                      {circleUser.phoneNumber?.trim()
                                        ? circleUser.phoneNumber
                                        : "—"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                                      }}
                                    >
                                      {formatDate(circleUser.joinedAt)}
                                    </Typography>
                                    <Box
                                      className="action-buttons"
                                      sx={MANAGEMENT_ACTION_ROW_SX}
                                    >
                                      <Button
                                        variant="text"
                                        size="small"
                                        onClick={() =>
                                          setAliasDialogUser(circleUser)
                                        }
                                        sx={{
                                          minWidth: "auto",
                                          px: 0.75,
                                          textTransform: "none",
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                        }}
                                      >
                                        Edit alias
                                      </Button>
                                      <IconButton
                                        className="delete-button"
                                        onClick={() =>
                                          setUserPendingRemove(circleUser)
                                        }
                                        disabled={
                                          removingUserId === circleUser.userId
                                        }
                                        size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                                        sx={{ color: lightTheme.colors.error }}
                                        aria-label={`Remove ${circleUser.name} from circle`}
                                      >
                                        {removingUserId === circleUser.userId ? (
                                          <CircularProgress
                                            size={16}
                                            thickness={5}
                                          />
                                        ) : (
                                          <DeleteIcon
                                            fontSize={
                                              MANAGEMENT_ACTION_ICON_FONT_SIZE
                                            }
                                          />
                                        )}
                                      </IconButton>
                                    </Box>
                                  </Box>
                                )}
                              </Paper>
                            );
                          })}
                        </Box>
                        {visibleCircleUsers.length < membersSourceLength && (
                          <Box
                            ref={membersSentinelRef}
                            sx={{ mt: 2, pt: 1, textAlign: "center", color: "text.secondary" }}
                          >
                            <Typography variant="caption">Loading more...</Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 2, textAlign: "center" }}
                        >
                          {filteredCircleUsers.length === 0
                            ? "No members to show."
                            : (() => {
                                const start = 1;
                                const end = visibleCircleUsers.length;
                                return `Showing ${start}-${end} of ${filteredCircleUsers.length} member${filteredCircleUsers.length !== 1 ? "s" : ""}`;
                              })()}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                <Accordion
                  expanded={currentMembersExpanded}
                  onChange={(_, isExpanded) =>
                    setCurrentMembersExpanded(isExpanded)
                  }
                  sx={{
                    boxShadow: "none",
                    border: { xs: "none", sm: "1px solid" },
                    borderColor: { sm: "divider" },
                    borderRadius: { xs: 0, sm: 2 },
                    backgroundColor: "background.paper",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: { xs: 0.5, sm: 2 },
                      py: { xs: 1, sm: 1.5 },
                      cursor: "pointer",
                      "& .MuiAccordionSummary-content": {
                        my: 0,
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.secondary.main }}
                    >
                      Current Members ({circleUsers.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: { xs: 0, sm: 2 }, pt: 0, pb: { xs: 1, sm: 2 } }}
                  >
                    {circleUsers.length === 0 ? (
                      <Box
                        sx={{
                          p: 4,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        <GroupsIcon
                          sx={{
                            fontSize: 40,
                            mb: 2,
                            opacity: 0.3,
                            color: "text.secondary",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: (theme) => theme.typography.body2.fontSize,
                            opacity: 0.7,
                          }}
                        >
                          No users in this circle yet.
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: MEMBER_LIST_CARD_GAP,
                        }}
                      >
                        {visibleCircleUsers.map((circleUser) => {
                          const aliasField = (
                            <TextField
                              size="small"
                              placeholder="Alias (optional)"
                              title={`How they appear in ${subcircleName}`}
                              fullWidth={isMobile}
                              value={aliasDrafts[circleUser.userId] ?? ""}
                              onChange={(e) => {
                                handleAliasDraftChange(
                                  circleUser.userId,
                                  e.target.value
                                );
                                scheduleAliasSave(circleUser.userId);
                              }}
                              onBlur={() => flushAliasSave(circleUser.userId)}
                              inputProps={{ maxLength: 60 }}
                              sx={{
                                ...(!isMobile && {
                                  flex: "1 1 168px",
                                  minWidth: 168,
                                  maxWidth: 280,
                                  width: "100%",
                                  "& .MuiInputBase-root": {
                                    height: 40,
                                    fontSize: "0.8125rem",
                                  },
                                }),
                              }}
                              InputProps={{
                                sx: {
                                  borderRadius: 2,
                                  bgcolor: "background.paper",
                                },
                                endAdornment:
                                  savingAliasUserId === circleUser.userId ? (
                                    <InputAdornment position="end">
                                      <CircularProgress
                                        color="inherit"
                                        size={16}
                                        thickness={5}
                                      />
                                    </InputAdornment>
                                  ) : undefined,
                              }}
                            />
                          );

                          const memberMeta = (
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                component="div"
                                sx={{
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  color: "text.primary",
                                }}
                              >
                                {circleUser.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2 }}
                              >
                                Joined {formatDate(circleUser.joinedAt)}
                              </Typography>
                              <Typography
                                variant="body2"
                                component="div"
                                color="text.secondary"
                                noWrap
                                title={
                                  circleUser.email?.trim()
                                    ? circleUser.email
                                    : undefined
                                }
                                sx={{
                                  fontSize: "0.8125rem",
                                  lineHeight: 1.45,
                                }}
                              >
                                {circleUser.email}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}
                              >
                                {circleUser.phoneNumber?.trim()
                                  ? circleUser.phoneNumber
                                  : "N/A"}
                              </Typography>
                            </Box>
                          );

                          return (
                            <Paper
                              key={circleUser.userId}
                              elevation={0}
                              sx={memberListCardSx}
                            >
                              {isMobile ? (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      justifyContent: "space-between",
                                      gap: 1.5,
                                      width: "100%",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1.5,
                                        flex: 1,
                                        minWidth: 0,
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          width: MEMBER_CARD_AVATAR_SIZE,
                                          height: MEMBER_CARD_AVATAR_SIZE,
                                          flexShrink: 0,
                                          mt: 0.125,
                                          backgroundColor: getAvatarColor(
                                            circleUser.name,
                                            lightTheme
                                          ),
                                          color: "common.white",
                                          fontSize: "0.9375rem",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {getInitials(circleUser.name)}
                                      </Avatar>
                                      {memberMeta}
                                    </Box>
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => setUserPendingRemove(circleUser)}
                                      disabled={
                                        removingUserId === circleUser.userId
                                      }
                                      aria-label="Remove user"
                                      sx={{ p: 0.5, flexShrink: 0, mt: -0.25 }}
                                    >
                                      {removingUserId === circleUser.userId ? (
                                        <CircularProgress size={18} thickness={5} />
                                      ) : (
                                        <DeleteIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Box>
                                  {aliasField}
                                </>
                              ) : (
                                <>
                                  <Avatar
                                    sx={{
                                      width: MEMBER_CARD_AVATAR_SIZE,
                                      height: MEMBER_CARD_AVATAR_SIZE,
                                      flexShrink: 0,
                                      mt: 0.125,
                                      backgroundColor: getAvatarColor(
                                        circleUser.name,
                                        lightTheme
                                      ),
                                      color: "common.white",
                                      fontSize: "0.9375rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {getInitials(circleUser.name)}
                                  </Avatar>
                                  {memberMeta}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "center",
                                      gap: 1.5,
                                      flexShrink: 0,
                                      ml: "auto",
                                      minWidth: 0,
                                      maxWidth: { sm: 380 },
                                    }}
                                  >
                                    {aliasField}
                                    <IconButton
                                      className="delete-button"
                                      onClick={() => setUserPendingRemove(circleUser)}
                                      disabled={
                                        removingUserId === circleUser.userId
                                      }
                                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                                      sx={{
                                        color: lightTheme.colors.error,
                                        flexShrink: 0,
                                      }}
                                      aria-label={`Remove ${circleUser.name} from circle`}
                                    >
                                      {removingUserId === circleUser.userId ? (
                                        <CircularProgress size={18} thickness={5} />
                                      ) : (
                                        <DeleteIcon
                                          fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE}
                                        />
                                      )}
                                    </IconButton>
                                  </Box>
                                </>
                              )}
                            </Paper>
                          );
                        })}
                        {visibleCircleUsers.length < membersSourceLength && (
                          <Box
                            ref={membersSentinelRef}
                            sx={{ mt: 2, pt: 1, textAlign: "center", color: "text.secondary" }}
                          >
                            <Typography variant="caption">Loading more...</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
              </Box>
            )}
          </Box>
            );
            if (!isPageLayout) {
              return memberFlex;
            }
            return (
              <Paper
                elevation={0}
                sx={{
                  mb: { xs: 2, md: 0 },
                  border: { xs: "none", md: "1px solid" },
                  borderColor: { md: "divider" },
                  borderRadius: { xs: 0, md: 2 },
                  overflow: { xs: "visible", md: "hidden" },
                  backgroundColor: { xs: "transparent", md: "background.paper" },
                }}
              >
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    borderRadius: { xs: 2, md: 0 },
                    px: { xs: 1, md: 0 },
                  }}
                >
                  <Tabs
                    value={desktopTab}
                    onChange={(_, next) => setDesktopTab(next)}
                    variant={isMobile ? "fullWidth" : "scrollable"}
                    scrollButtons={isMobile ? false : "auto"}
                    allowScrollButtonsMobile={!isMobile}
                    sx={{
                      minHeight: { xs: 44, sm: 48 },
                      "& .MuiTab-root": {
                        minHeight: { xs: 44, sm: 48 },
                        px: { xs: 0.75, sm: 2 },
                        fontSize: { xs: "0.76rem", sm: "0.875rem" },
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <Tab
                      value="add-users"
                      label={
                        isMobile
                          ? `Add (${availableUsers.length})`
                          : `ADD USERS (${availableUsers.length})`
                      }
                    />
                    <Tab
                      value="current-members"
                      label={
                        isMobile
                          ? `Members (${circleUsers.length})`
                          : `CURRENT MEMBERS (${circleUsers.length})`
                      }
                    />
                  </Tabs>
                </Box>
                <Box sx={{ pt: { xs: 1.25, md: 0 } }}>{memberFlex}</Box>
              </Paper>
            );
          })()}
        </Box>
    );

    const aliasEditDialog = (
      <Dialog
        open={aliasDialogUser !== null}
        onClose={() => setAliasDialogUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Display name in {subcircleName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Alias (optional)"
            fullWidth
            value={aliasDialogValue}
            onChange={(e) => setAliasDialogValue(e.target.value)}
            inputProps={{ maxLength: 60 }}
            helperText="How this member appears inside this circle."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAliasDialogUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!!savingAliasUserId}
            onClick={() => void saveAliasFromDialog()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );

    const removeCircleMemberDialog = (
      <DeleteConfirmationDialog
        user={
          userPendingRemove
            ? { id: userPendingRemove.userId, name: userPendingRemove.name }
            : null
        }
        removeFromContextName={subcircleName}
        onClose={() => setUserPendingRemove(null)}
        onConfirm={(userId) => {
          if (!userPendingRemove || userPendingRemove.userId !== userId) {
            return;
          }
          const toRemove = userPendingRemove;
          void (async () => {
            const ok = await handleRemoveUser(toRemove);
            if (ok) setUserPendingRemove(null);
          })();
        }}
        isRemoving={
          !!userPendingRemove &&
          removingUserId === userPendingRemove.userId
        }
      />
    );

    const historicalAccessModal = (
      <ActionModal
        open={userPendingAdd !== null}
        onClose={() => {
          if (!addingUserId) {
            setUserPendingAdd(null);
            setPendingAddAlias("");
          }
        }}
        disableBackdropClick={!!addingUserId}
        title="Historical message access"
        subtitle={
          userPendingAdd
            ? `Adding ${userPendingAdd.name} to ${subcircleName}`
            : undefined
        }
        maxWidth="sm"
        footer={
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => !addingUserId && setUserPendingAdd(null)}
              disabled={!!addingUserId}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void confirmAddPendingUser()}
              disabled={!!addingUserId}
            >
              {addingUserId ? "Adding..." : "Add user"}
            </Button>
          </Box>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Display alias (optional)"
            placeholder="e.g. John's Mom"
            value={pendingAddAlias}
            onChange={(e) => setPendingAddAlias(e.target.value)}
            inputProps={{ maxLength: 60 }}
            helperText="Shown in this circle instead of their account name (max 60 characters)"
            fullWidth
            disabled={!!addingUserId}
          />
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: lightTheme.colors.bootstrapPrimaryTint05,
              borderRadius: 2,
              border: `1px solid ${lightTheme.colors.bootstrapPrimaryTint20}`,
            }}
          >
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={legacyAccessOption}
                onChange={(e) =>
                  setLegacyAccessOption(
                    e.target.value as "none" | "7days" | "all"
                  )
                }
              >
                <FormControlLabel
                  value="none"
                  control={<Radio color="primary" />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        No access to historical messages
                      </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      Users will only see notices created from the day they join
                      onwards.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: "flex-start" }}
              />
              <FormControlLabel
                value="7days"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Allow access to historical messages from last 7 days
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      Users will see notices from the last 7 days before they
                      join, plus all future notices.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: "flex-start" }}
              />
              <FormControlLabel
                value="all"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Allow access to all historical messages
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      Users will see all notices from this circle, including
                      messages from before they joined.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start" }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </ActionModal>
    );

    if (isPageLayout) {
      return (
        <>
          {panelBody}
          {aliasEditDialog}
          {removeCircleMemberDialog}
          {historicalAccessModal}
        </>
      );
    }

    return (
      <>
        <ActionModal
          open={open}
          onClose={onClose}
          title={`Manage Users in ${subcircleName}`}
          maxWidth="lg"
          paperSx={{
            maxHeight: { xs: undefined, sm: "98vh" },
          }}
          dialogContentSx={{
            minHeight: { xs: "auto", sm: "min(72vh, 820px)" },
            maxHeight: { xs: "none", sm: "calc(98vh - 120px)" },
          }}
        >
          {panelBody}
        </ActionModal>
        {aliasEditDialog}
        {removeCircleMemberDialog}
        {historicalAccessModal}
      </>
    );
  }
);

ManageCircleUsers.displayName = "ManageCircleUsers";
